import * as React from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import { Button, Text } from '@metrisk/react_metrisk-components'

import campaign from '@app/config/campaign'
import Field from '@components/Field'

const purchaseFieldsOrder = ['invoiceNo', 'orderNo', 'purchaseDate', 'placeOfPurchase', 'proofOfPurchase', 'proofOfPackaging', 'proofOfProduct', 'purchaseItems']

const PurchaseInformationItem = ({ index, item, upload, formFields, fieldsToRender, canDelete, onItemUpdate, onDelete }: any) => {
  const intl = useIntl()
  const purchaseFields: any = {
    orderNo: {
      type: 'text',
      label: intl.formatMessage({ defaultMessage: 'Order Number' }),
      validation: (value: string) => /^([0-9]{9}|[A-Za-z0-9]{12})$/.test(value),
      msg: intl.formatMessage({ defaultMessage: 'Please enter a valid order number' }),
    },
    invoiceNo: {
      type: 'text',
      label: intl.formatMessage({ defaultMessage: 'Receipt Number' }),
      validation: (value: any) => value && value.trim().length > 0,
    },
    purchaseDate: {
      type: 'date',
      label: intl.formatMessage({ defaultMessage: 'Purchase Date' }),
      validation: (value: any) => value && value.trim().length > 0,
    },
    placeOfPurchase: {
      type: 'text',
      label: intl.formatMessage({ defaultMessage: 'Place of Purchase' }),
      validation: (value: any) => value && value.trim().length > 0,
    },
    proofOfPurchase: {
      type: 'upload',
      label: intl.formatMessage({ defaultMessage: 'Proof of Purchase' }),
    },
    proofOfPackaging: {
      type: 'upload',
      label: intl.formatMessage({ defaultMessage: 'Proof of Packaging' }),
    },
    proofOfProduct: {
      type: 'upload',
      label: intl.formatMessage({ defaultMessage: 'Proof of Product' }),
    },
    purchaseItems: {
      type: 'purchaseItems',
      label: null,
    },
  }
  
  const itemFields = purchaseFieldsOrder.filter((field) => fieldsToRender.includes(field))

  const getValue = (field: string) => {
    return item.fields[field]?.value
  }

  const isValid = (field: string, value: any): boolean => {
    if (value === null) return false

    const formField = formFields.find((formField: any) => formField.id === field)
    if (formField?.regex) {
      return new RegExp(formField.regex).test(value)
    }

    if (purchaseFields[field]?.validation) {
      return purchaseFields[field]?.validation(value)
    }

    if (Array.isArray(value)) {
      return value.every((item) => item.valid)
    }

    return Boolean(value)
  }

  const handleChange = (field: string, value: any) => {
    onItemUpdate(item.id, field, value, isValid(field, value))
  }

  const handleDelete = () => {
    onDelete(item.id)
  }

  return (
    <div className="purchase-information-item">
      <div className="purchase-information-item__header">
        {campaign.maxRedemptions !== 1 && <Text tag="h5" type={'Delta'}>
          <FormattedMessage defaultMessage={'Receipt Item'} /> #{index + 1}
        </Text>}
        {canDelete && (
          <Button type={'Secondary'} className="purchase-information-item__remove" onClick={handleDelete}>
            <FormattedMessage defaultMessage={'Remove Receipt'} />
          </Button>
        )}
      </div>

      {itemFields.map((field: string) => {
        const formField = formFields.find((formField: any) => formField.id === field)
        const isReadOnly = Boolean(item?.fields[field]?.readOnly)

        return (
          <Field
            key={`${item.id}-${field}`}
            type={purchaseFields[field].type}
            label={formField ? formField.props.label : purchaseFields[field].label}
            value={getValue(field)}
            upload={upload}
            fieldsToRender={fieldsToRender}
            formFields={formFields}
            valid={getValue(field) === null ? true : isValid(field, getValue(field))}
            msg={getValue(field) && !isValid(field, getValue(field)) && purchaseFields[field].msg}
            required={isReadOnly ? false : true}
            readOnly={isReadOnly}
            onChange={(value: any) => handleChange(field, value)}
          />
        )
      })}
    </div>
  )
}

export default PurchaseInformationItem
